import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { CalendarBlank, UserCircle, Warning } from '@phosphor-icons/react';
import {
  borderClassFor,
  entryHours,
  entryUnits,
  formatWeekRange,
  readinessBadgeClass,
  readinessBadgeLabel,
  sortEntriesChronologically,
  STATUS_MAP,
} from './constants';
import { ScanThumbnail } from './ScanThumbnail';
import DocumentPreviewModal from './DocumentPreviewModal';
import MatchingRow from './MatchingRow';
import VisitsSection from './VisitsSection';
import CardFooter from './CardFooter';
import { SanityFlagsBanner } from '../../components/timesheet/SanityFlagsBanner';
import TotalReconciliationBanner from './TotalReconciliationBanner';
import { formatDate } from '../../lib/utils';

const initialEntriesFor = (confirmed, extracted) =>
  sortEntriesChronologically(confirmed.entries || extracted.entries || []).map((e, i) => ({
    _idx: i,
    date: e.date || '',
    day_of_week: e.day_of_week || '',
    time_in: e.time_in || '',
    time_out: e.time_out || '',
    total_hours: e.total_hours ?? null,
    tasks: e.tasks || [],
    notes: e.notes || '',
  }));

const TimesheetCard = ({
  ts,
  patients,
  employees,
  onInlineSave,
  onConfirm,
  onDelete,
  onSubmitEvv,
  onCreateClaims,
  onViewClaims,
  busy,
}) => {
  const extracted = ts.extracted_data || {};
  const confirmed = ts.confirmed_data || {};
  const initialEntries = useMemo(
    () => initialEntriesFor(confirmed, extracted),
    // `confirmed` and `extracted` are stable per-render derivatives of
    // `ts.*_data`; the only fields that meaningfully change are their
    // ``entries`` arrays, and those two changes are what should
    // trigger the recompute. Listing `confirmed`/`extracted`
    // themselves is redundant and would spam re-computes on every
    // parent render since the shallow derived objects are new each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
    [confirmed.entries, extracted.entries]
  );

  const [patientId, setPatientId] = useState(
    ts.inline_patient_id || ts.patient_match?.id || ''
  );
  const [employeeId, setEmployeeId] = useState(
    ts.inline_employee_id || ts.employee_match?.id || ''
  );
  const [entries, setEntries] = useState(initialEntries);
  const [aideSig, setAideSig] = useState(
    ts.aide_signature_completed ?? !!extracted.signature_present
  );
  const [supSig, setSupSig] = useState(
    ts.supervisor_signature_completed ?? !!extracted.supervisor_signature_present
  );
  const [saving, setSaving] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const st = STATUS_MAP[ts.status] || {
    label: ts.status,
    color: 'bg-slate-100 text-slate-700',
  };
  const totalUnits = entries.reduce((a, e) => a + entryUnits(e), 0);
  const totalHours = entries.reduce((a, e) => a + entryHours(e), 0);
  const editable = ts.status === 'pending_review';
  const readiness = ts.readiness || null;
  const borderClass = borderClassFor(editable, readiness?.level);

  const selectedPatient = patients.find((p) => p.id === patientId);
  const selectedEmployee = employees.find((e) => e.id === employeeId);

  const displayPatientName = selectedPatient
    ? `${selectedPatient.first_name} ${selectedPatient.last_name}`
    : extracted.patient_name || 'Unknown Patient';

  const displayEmployeeName = selectedEmployee
    ? `${selectedEmployee.first_name} ${selectedEmployee.last_name}`
    : extracted.staff_name || null;

  const weekRange = formatWeekRange(extracted);

  const updateEntry = (idx, field, value) => {
    setEntries((prev) =>
      prev.map((e) => (e._idx === idx ? { ...e, [field]: value } : e))
    );
  };

  const addEntry = () => {
    setEntries((prev) => [
      ...prev,
      {
        _idx: Math.max(0, ...prev.map((e) => e._idx)) + 1,
        date: '',
        day_of_week: '',
        time_in: '',
        time_out: '',
        total_hours: null,
        tasks: [],
        notes: '',
      },
    ]);
  };

  const removeEntry = (idx) => {
    setEntries((prev) => prev.filter((e) => e._idx !== idx));
  };

  const stripIdx = (rows) => rows.map(({ _idx, ...rest }) => rest);

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await onInlineSave(ts.id, {
        patient_id: patientId || null,
        employee_id: employeeId || null,
        entries: stripIdx(entries),
        aide_signature_completed: aideSig,
        supervisor_signature_completed: supSig,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmClick = async () => {
    if (!patientId) {
      toast.error('Please select a patient first');
      return;
    }
    if (!employeeId) {
      toast.error('Please select an employee first');
      return;
    }
    setSaving(true);
    try {
      await onConfirm(ts.id, {
        patient_id: patientId,
        employee_id: employeeId,
        entries: stripIdx(entries),
        total_weekly_hours: totalHours,
        week_start_date: extracted.week_start_date,
        week_end_date: extracted.week_end_date,
        aide_signature_completed: aideSig,
        supervisor_signature_completed: supSig,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`bg-white border border-slate-200 border-l-4 ${borderClass} rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden`}
      data-testid={`timesheet-card-${ts.id}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 via-white to-white px-5 py-4 border-b border-slate-200">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-3 mb-1">
              <ScanThumbnail
                ts={ts}
                alt={`Scanned page for ${displayPatientName}`}
                onOpenPreview={() => setLightboxOpen(true)}
              />
              <div className="min-w-0 flex-1">
                <h3
                  className="text-lg font-bold text-slate-900 truncate"
                  data-testid={`patient-name-${ts.id}`}
                  title={displayPatientName}
                >
                  {displayPatientName}
                </h3>
                <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                  {weekRange && (
                    <span className="inline-flex items-center gap-1">
                      <CalendarBlank size={12} />
                      {weekRange}
                    </span>
                  )}
                  {weekRange && <span>·</span>}
                  <span className="truncate max-w-[300px]" title={ts.filename}>
                    {ts.filename}
                  </span>
                </div>
                {displayEmployeeName && (
                  <p className="text-xs text-slate-600 mt-1 flex items-center gap-1">
                    <UserCircle size={12} className="text-slate-400" />
                    <span className="font-medium">{displayEmployeeName}</span>
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {readiness && editable && (
              <Badge
                className={`text-[10px] ${readinessBadgeClass(readiness.level)}`}
                title={readiness.reason}
                data-testid={`readiness-${ts.id}`}
              >
                {readinessBadgeLabel(readiness.level)} · {readiness.confidence}%
              </Badge>
            )}
            {/* Total-reconciliation pill — one-glance verdict from the
                backend total_reconciliation.status field. Verified in
                green (safe to confirm), mismatch in amber (needs
                review). Absent when there's no data (row still
                processing) or when the sheet had no stated total to
                compare against. */}
            {ts.total_reconciliation?.status === 'verified' && (
              <Badge
                className="text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200"
                title={ts.total_reconciliation.message}
                data-testid={`reconciliation-badge-${ts.id}`}
              >
                Verified
              </Badge>
            )}
            {ts.total_reconciliation?.status === 'mismatch' && (
              <Badge
                className="text-[10px] bg-amber-100 text-amber-800 border border-amber-200"
                title={ts.total_reconciliation.message}
                data-testid={`reconciliation-badge-${ts.id}`}
              >
                Total mismatch
              </Badge>
            )}
            <Badge className={`${st.color} text-xs`}>{st.label}</Badge>
            {ts.source_document_status === 'retention_failed' && (
              <Badge
                className="text-[10px] bg-amber-100 text-amber-800 border border-amber-200"
                title="Original source document could not be retained. Review required — confirmation is blocked until re-upload."
                data-testid={`retention-failed-${ts.id}`}
              >
                Source not retained
              </Badge>
            )}
            <div className="text-right">
              <p className="text-2xl font-bold text-indigo-900 leading-none">
                {totalUnits.toFixed(0)}
              </p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">units</p>
            </div>
          </div>
        </div>
      </div>

      <MatchingRow
        tsId={ts.id}
        editable={editable}
        patientId={patientId}
        setPatientId={setPatientId}
        employeeId={employeeId}
        setEmployeeId={setEmployeeId}
        patients={patients}
        employees={employees}
        ts={ts}
        displayPatientName={displayPatientName}
        displayEmployeeName={displayEmployeeName}
      />

      <VisitsSection
        tsId={ts.id}
        entries={entries}
        editable={editable}
        onUpdate={updateEntry}
        onAdd={addEntry}
        onRemove={removeEntry}
      />

      {/* Same-agency duplicate upload warning (SHA-256 content match).
          Warning only — reviewing/confirming stays fully allowed. */}
      {ts.source_duplicate && (
        <div
          data-testid={`duplicate-warning-${ts.id}`}
          className="flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5"
          role="alert"
        >
          <Warning size={14} weight="fill" className="shrink-0 mt-0.5 text-amber-500" />
          <span>
            <span className="font-semibold">Possible duplicate</span> — this file matches
            {ts.source_duplicate.filename ? ` "${ts.source_duplicate.filename}"` : ' a previous upload'}
            {ts.source_duplicate.uploaded_at ? ` uploaded ${formatDate(ts.source_duplicate.uploaded_at)}` : ''}.
            Check it isn't already in review before confirming.
          </span>
        </div>
      )}

      {/* Backend-calculated total reconciliation. Compares the sheet's
          stated TOTAL against the sum of daily entries computed
          server-side. Verified rows show a compact green pill; any
          mismatch renders a side-by-side breakdown so the operator
          can spot the wrong entry without opening the lightbox. */}
      <TotalReconciliationBanner
        reconciliation={ts.total_reconciliation}
        tsId={ts.id}
      />

      <SanityFlagsBanner
        timesheetId={ts.id}
        sanityReport={ts.sanity_report}
        override={ts.sanity_override}
        expectedConfirmToken={`OVERRIDE-${(ts.id || '').slice(0, 8)}`}
        onOverrideRecorded={(data) => {
          // Mutate local prop optimistically so the banner repaints with
          // the recorded override; the next fetch from the parent will
          // reconcile authoritatively.
          if (data?.override) {
            ts.sanity_override = data.override;
          }
        }}
      />

      <CardFooter
        ts={ts}
        editable={editable}
        aideSig={aideSig}
        setAideSig={setAideSig}
        supSig={supSig}
        setSupSig={setSupSig}
        totalHours={totalHours}
        totalUnits={totalUnits}
        busy={busy}
        saving={saving}
        onDelete={onDelete}
        onSaveDraft={handleSaveDraft}
        onConfirm={handleConfirmClick}
        onSubmitEvv={onSubmitEvv}
        onCreateClaims={onCreateClaims}
        onViewClaims={onViewClaims}
      />

      {lightboxOpen && (
        <DocumentPreviewModal
          ts={ts}
          patientName={displayPatientName}
          employeeName={displayEmployeeName}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
};

export default TimesheetCard;
